"use client";

import React from "react";
import { ArrowRight, Calendar, Globe, Crosshair } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const historicalEras = [
  {
    id: "era-1",
    title: "The HomeKit Foundation",
    date: "Since 2014",
    region: "Cupertino, CA",
    description:
      "Apple's framework for communicating with and controlling connected accessories. It prioritizes privacy and local control, ensuring your home data stays within your walls while offering seamless integration.",
    specs: {
      ignition: "Local Encryption",
      caliber: "End-to-End Security",
      rate: "Instant Response",
    },
  },
  {
    id: "era-2",
    title: "The Matter Standard",
    date: "c. 2022",
    region: "Global Alliance",
    description:
      "A unifying industry standard that allows smart devices from different ecosystems to work together. Moza Casa leverages Matter to ensure your HomeKit setup is future-proof and versatile.",
    specs: {
      ignition: "Thread Protocol",
      caliber: "Cross-Platform",
      rate: "Universal Sync",
    },
  },
];

const archiveImages = [
  {
    id: "img-1",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/smart_home_hub_minimal.png",
    alt: "Minimalist Home Hub Setup",
    caption: "A clean, distraction-free central hub for managing your Apple HomeKit ecosystem.",
    year: "2024",
  },
  {
    id: "img-2",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/automated_lighting_scene.png",
    alt: "Automated Ambient Lighting",
    caption: "Simple living through adaptive lighting that follows your natural circadian rhythm.",
    year: "2024",
  },
  {
    id: "img-3",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/smart_lock_security_interface.png",
    alt: "Secure Entry Interface",
    caption: "The intersection of security and simplicity: keyless entry managed via iPhone.",
    year: "2024",
  },
];

export default function HistoricalOrigins() {
  const { ref: sectionRef, isVisible } = useRevealOnScroll({ threshold: 0.1 });

  return (
    <section data-section-id="238592" id="posts" className="bg-background text-foreground w-full">
      {}
      <div className="bg-primary text-primary-foreground py-24 w-full overflow-hidden">
        <div
          ref={sectionRef}
          className={`container mx-auto px-6 md:px-12 lg:px-24 transition-all duration-1000 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div className="space-y-6">
                <Badge variant="outline" className="border-secondary text-secondary font-mono tracking-widest uppercase">
                  The Moza Method
                </Badge>
                <h2 className="font-default text-4xl md:text-5xl font-bold leading-tight text-primary-foreground">
                  Philosophy of <span className="text-secondary italic">Simple Living</span>
                </h2>
                <p className="text-primary-foreground/70 text-lg leading-relaxed font-default">
                  Discover the art of home automation without the complexity. We focus on Apple HomeKit technology to create environments that respond to your needs intuitively and elegantly.
                </p>
              </div>

              <Separator className="bg-secondary/20" />

              <div className="flex flex-col gap-6">
                {historicalEras.map((era, i) => (
                  <Card
                    key={era.id}
                    data-index={i}
                    className="bg-background/5 backdrop-blur-md border-secondary/30 shadow-lg shadow-black/10 rounded-lg"
                  >
                    <CardHeader className="pb-4">
                      <div className="flex justify-between items-start mb-2">
                        <Badge className="bg-secondary/20 text-secondary hover:bg-secondary/30 border-none font-mono rounded-md">
                          {era.date}
                        </Badge>
                        <div className="flex items-center gap-1.5 text-primary-foreground/60 text-sm font-mono">
                          <Globe className="size-3.5" />
                          <span>{era.region}</span>
                        </div>
                      </div>
                      <CardTitle className="font-default text-xl text-primary-foreground">
                        {era.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-primary-foreground/70 text-sm leading-relaxed font-default">
                        {era.description}
                      </p>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary/10">
                        <div className="space-y-1">
                          <span className="text-xs text-primary-foreground/50 uppercase tracking-wider font-mono">Protocol</span>
                          <p className="text-sm text-primary-foreground font-mono">{era.specs.ignition}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-primary-foreground/50 uppercase tracking-wider font-mono">Standard</span>
                          <p className="text-sm text-primary-foreground font-mono">{era.specs.caliber}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="pt-4">
                <Link to="#">
                  <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground rounded-full group">
                    View Automation Recipes
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>

            {}
            <div className="lg:col-span-7 relative">
              <div className="absolute -inset-4 bg-secondary/5 blur-3xl rounded-full pointer-events-none" />
              
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full relative z-10"
              >
                <CarouselContent>
                  {archiveImages.map((item, i) => (
                    <CarouselItem key={item.id} data-index={i}>
                      <div className="relative group overflow-hidden border border-secondary/20 bg-background/10 p-2 rounded-2xl">
                        <div className="relative aspect-[4/3] overflow-hidden bg-black/50 rounded-xl">
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                          />
                          
                          {}
                          <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                            <Badge className="bg-black/60 backdrop-blur-md text-secondary border-secondary/50 font-mono rounded-full">
                              <Calendar className="mr-1.5 size-3" />
                              {item.year}
                            </Badge>
                            <Badge className="bg-black/60 backdrop-blur-md text-primary-foreground border-white/10 font-mono rounded-full">
                              <Crosshair className="mr-1.5 size-3" />
                              Feature {i + 1}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="p-6 bg-background/5 backdrop-blur-sm border-t border-secondary/20 mt-2 rounded-b-xl">
                          <h3 className="font-default text-lg font-semibold text-primary-foreground mb-2">
                            {item.alt}
                          </h3>
                          <p className="text-primary-foreground/60 text-sm font-mono leading-relaxed">
                            {item.caption}
                          </p>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                
                <div className="flex items-center justify-end gap-4 mt-6 pr-2">
                  <CarouselPrevious className="relative inset-0 translate-y-0 size-10 rounded-full border-secondary/50 text-secondary hover:bg-secondary hover:text-secondary-foreground bg-transparent" />
                  <CarouselNext className="relative inset-0 translate-y-0 size-10 rounded-full border-secondary/50 text-secondary hover:bg-secondary hover:text-secondary-foreground bg-transparent" />
                </div>
              </Carousel>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}